import React, { useState } from "react";
import {
  CheckSquare,
  Edit3,
  Trash2,
  ShieldCheck,
  AlertTriangle,
  Layers,
  Check,
  Square,
  X,
  User,
  Ruler,
  Gem,
  PenTool,
  AlertCircle,
  Siren,
  ChevronDown, // New Import
  ChevronRight, // New Import
} from "lucide-react";
import DaysBadge from "./DaysBadge";
import TransitToggle from "./TransitToggle";
import ProductionItemRow from "./ProductionItemRow";
import {
  PRODUCTION_STATUS_CONFIG,
  KANBAN_ORDER,
} from "../../config/productionStatuses";

// Helper de Data
const getIsoDate = (val) => {
  if (!val) return "";
  if (val.toDate) return val.toDate().toISOString().split("T")[0];
  if (typeof val === "string") {
    // Suporta tanto 2024-01-01T... quanto 2024-01-01 ...
    return val.split("T")[0].split(" ")[0];
  }
  return "";
};

// Helper Anti-ND
const hasValue = (val) => {
  if (!val) return false;
  const s = String(val).trim().toUpperCase();
  return s !== "" && s !== "ND" && s !== "-" && s !== "N/A";
};

// --- COMPONENTE INTERNO: STATUS GROUP (ACCORDION) ---
// Melhora performance escondendo itens de listas grandes
const StatusGroup = ({
  title,
  count,
  colorClass,
  children,
  headerActions, // Botões extras no header (ex: Select All)
  isOrphan = false,
  initialOpen = true,
}) => {
  // Controle de estado inicial via props
  const [isOpen, setIsOpen] = useState(initialOpen);

  return (
    <div
      className={`rounded-xl shadow-sm border overflow-hidden mb-6 transition-all ${isOrphan ? "bg-red-50 border-red-200" : "bg-white border-slate-200"
        }`}
    >
      {/* HEADER CLICÁVEL */}
      <div
        className={`px-4 py-3 flex justify-between items-center cursor-pointer transition-colors ${colorClass} ${!isOpen ? "opacity-90 hover:opacity-100" : ""
          }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          {/* Ícone de Expandir/Recolher */}
          <div className="text-black/70 hover:text-white transition-colors">
            {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>

          <span className="font-bold text-sm tracking-wide uppercase flex items-center gap-2">
            {title}
          </span>

          <span className="bg-white/20 px-2 py-0.5 rounded text-xs font-bold text-white">
            {count}
          </span>
        </div>

        {/* Ações do Header (ex: Select All) - Clicar aqui não deve fechar o accordion */}
        <div onClick={(e) => e.stopPropagation()}>{headerActions}</div>
      </div>

      {/* CONTEÚDO (Só renderiza se estiver aberto = PERFORMANCE) */}
      {isOpen && (
        <div
          className={`divide-y ${isOrphan ? "divide-red-100" : "divide-slate-100"
            }`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default function ProductionListView({
  user,
  groupedOrders,
  selectedOrders,
  toggleSelect,
  setSelectSet,
  setEditingOrder,
  handleDeleteOrder,
  handleMoveStatus,
  findCatalogItem,
  onToggleTransit,
  onUpdateDate,
  onTogglePE,
}) {
  const allStatusKeys = Object.keys(groupedOrders);
  const orphanStatuses = allStatusKeys.filter(
    (status) =>
      !KANBAN_ORDER.includes(status) && groupedOrders[status].length > 0
  );

  return (
    <div className="space-y-4 w-full pb-20 animate-fade-in">
      {KANBAN_ORDER.map((statusId) => {
        const items = groupedOrders[statusId] || [];
        if (items.length === 0) return null;

        const config = PRODUCTION_STATUS_CONFIG[statusId] || {
          label: statusId,
          color: "bg-slate-500 text-white",
        };

        const allSelected = items.every((order) =>
          selectedOrders.has(order.id)
        );

        const toggleGroup = () => {
          const newSet = new Set(selectedOrders);
          if (allSelected) items.forEach((i) => newSet.delete(i.id));
          else items.forEach((i) => newSet.add(i.id));

          if (setSelectSet) setSelectSet(newSet);
          else items.forEach((i) => toggleSelect(i.id));
        };

        return (
          <StatusGroup
            key={statusId}
            title={config.label}
            count={items.length}
            colorClass={config.color}
            initialOpen={statusId !== "PEDIDO_PRONTO"}
            // Passamos o botão de "Marcar Todos" como prop para o Header
            headerActions={
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Impede que feche o accordion ao clicar aqui
                  toggleGroup();
                }}
                className="text-white/80 hover:text-white transition-colors p-1"
                title={allSelected ? "Desmarcar Todos" : "Marcar Todos"}
              >
                {allSelected ? <CheckSquare size={18} /> : <Square size={18} />}
              </button>
            }
          >
            {items.map((order) => {
              const isSelected = selectedOrders.has(order.id);
              return (
                <ProductionItemRow 
                  user={user}
                  key={order.id} 
                  order={order} 
                  isSelected={isSelected}
                  toggleSelect={toggleSelect}
                  handleMoveStatus={handleMoveStatus}
                  onToggleTransit={onToggleTransit}
                  handleDeleteOrder={handleDeleteOrder}
                  onTogglePE={onTogglePE}
                  setEditingOrder={setEditingOrder}
                  onUpdateDate={onUpdateDate}
                  findCatalogItem={findCatalogItem}
                />
              );
            })}
          </StatusGroup>
        );
      })}

      {orphanStatuses.map((statusId) => {
        const items = groupedOrders[statusId];
        const allSelected = items.every((order) =>
          selectedOrders.has(order.id)
        );
        const toggleGroup = () => {
          const newSet = new Set(selectedOrders);
          if (allSelected) items.forEach((i) => newSet.delete(i.id));
          else items.forEach((i) => newSet.add(i.id));
          if (setSelectSet) setSelectSet(newSet);
          else items.forEach((i) => toggleSelect(i.id));
        };

        return (
          <StatusGroup
            key={statusId}
            title={`STATUS DESCONHECIDO: "${statusId}"`}
            count={items.length}
            colorClass="bg-red-500 text-white" // Cor de erro
            isOrphan={true}
            headerActions={
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup();
                }}
                className="text-white/80 hover:text-white transition-colors p-1"
              >
                {allSelected ? <CheckSquare size={18} /> : <Square size={18} />}
              </button>
            }
          >
            {items.map((order) => {
              const isSelected = selectedOrders.has(order.id);
              return (
                <ProductionItemRow 
                  user={user}
                  key={order.id} 
                  order={order} 
                  isSelected={isSelected}
                  toggleSelect={toggleSelect}
                  handleMoveStatus={handleMoveStatus}
                  onToggleTransit={onToggleTransit}
                  handleDeleteOrder={handleDeleteOrder}
                  onTogglePE={onTogglePE}
                  setEditingOrder={setEditingOrder}
                  onUpdateDate={onUpdateDate}
                  findCatalogItem={findCatalogItem}
                />
              );
            })}
          </StatusGroup>
        );
      })}
    </div>
  );
}
